import { throttle } from '../../utils'
import { createGradient } from './createGradient'
import BezierEasing from 'bezier-easing'

interface Position {
  x: number
  y: number
}

interface Trace {
  createdAt: number
  position: Position
}
/**
 * Constants
 */

const GRADIENT_STEPS = 400
const SLOWDOWN = 20
const LIFESPAN = 8000
const RADIUS = 100
const RADIUS_APEX = 0.3

const gradient = createGradient(
  ['#ff8ea8', '#D1506E', '#ff8ea8'],
  GRADIENT_STEPS,
).getColors()

const gradientEasing = BezierEasing(0.5, 0, 0, 0.5)
const radiusEasingUp = BezierEasing(0, 1, 0.5, 1)
const radiusEasingDown = BezierEasing(0, 1, 0, 1)

/**
 * Utils
 */

const getColor = (start: number, end: number): string => {
  const diff = end / SLOWDOWN - start / SLOWDOWN
  const currentStep = Math.round(diff % GRADIENT_STEPS)
  const easedStep = gradientEasing(currentStep / GRADIENT_STEPS)
  const index = Math.round(easedStep * GRADIENT_STEPS)
  return gradient[index]
}

const getRadius = (_circleAge: number) => {
  const circleAge = Math.min(_circleAge, 1)
  if (circleAge < RADIUS_APEX) {
    const upAmount = circleAge / RADIUS_APEX
    const easeAmount = radiusEasingUp(upAmount)
    return Math.max(RADIUS * (circleAge / RADIUS_APEX) * easeAmount, 0)
  }
  const downAmount = 1 - (circleAge - RADIUS_APEX) / (1 - RADIUS_APEX)
  const easeDownAmount = radiusEasingDown(downAmount)
  // const radius = RADIUS * (1 - circleAge) * (1 - easeDownAmount)
  const radius =
    RADIUS *
    //
    (downAmount * easeDownAmount)
  //
  // (1 - easeDownAmount)
  return Math.max(radius, 0)
}

export const initCanvas = (canvas: HTMLCanvasElement) => {
  const documentBody = document.body
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Could not get canvas context')

  // eslint-disable-next-line
  let intervalTimer: any

  ctx.canvas.width = window.innerWidth
  ctx.canvas.height = window.innerHeight

  const circles: Set<Trace> = new Set()
  /**
   * Debug the gradient by rendering it to the canvas
   */

  // gradient.forEach((color, index) => {
  //   ctx.beginPath()
  //   ctx.fillStyle = color
  //   console.log(color)
  //   ctx.rect((index + 10) * 3, 10, 3, 30)
  //   ctx.fill()
  // })

  /**
   * Utilities
   */

  /**
   * Resets the canvas to its original state
   */
  function teardown() {
    documentBody.removeEventListener('mousemove', handleMouseMove)
    if (intervalTimer) clearInterval(intervalTimer)
    window.removeEventListener('message', handleMessage)
  }

  function reset() {
    circles.clear()
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height)
  }

  /**
   * Event Handlers
   */
  const handleMessage = (msg: MessageEvent) => {
    if (msg?.data?.payload?.event === 'fastRefreshScheduled') {
      reset()
    }
  }

  const handleMouseMove = throttle((ev: MouseEvent) => {
    const position = {
      x: ev.clientX,
      y: ev.clientY,
    }
    circles.add({ position, createdAt: new Date().getTime() })
  }, 50)

  window.addEventListener('message', handleMessage)
  documentBody.addEventListener('mousemove', handleMouseMove)

  const startTime = new Date().getTime()

  ctx.beginPath()
  ctx.arc(100, 75, 50, 0, 2 * Math.PI)
  ctx.stroke()

  const render = () => {
    const now = new Date().getTime()
    const color = getColor(startTime, now)

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    circles.forEach((circle, index) => {
      const { position, createdAt } = circle
      const { x, y } = position
      const circleAge = (now - createdAt) / LIFESPAN
      const radius = getRadius(circleAge)
      if (circleAge <= 1) {
        ctx.beginPath()
        ctx.fillStyle = color
        ctx.arc(x, y, radius, 0, 2 * Math.PI)
        ctx.fill()
      } else {
        circles.delete(circle)
      }
    })
  }

  intervalTimer = setInterval(render, 20)

  return teardown
}
